import { useState } from "react";
import { useTabDraft } from "@/components/ui/box-draft";
import type { Result } from "@/components/ui/box.schema";
import { checkCommit } from "@/lib/api/commitSeams";
import { ApiError } from "@/lib/api/client";

/** These coursework commands retain their exact request with the existing tab owner. */
export function usePolicyCommand<V, B>(args: { box: string; record: string; version: string | null; initial: V; action: string; subjects: string[] | ((body: B) => string[]); send: (body: B, key: string) => Promise<unknown>; effect: string | ((body: B) => string); changed: (body: B) => Promise<unknown> }) {
  type Send = { key: string; body: B; action: string; subjects: string[]; effect: string };
  const kept = useTabDraft<V, Send>(args);
  const [busyRecord, setBusyRecord] = useState<string | null>(null);
  const busy = busyRecord === args.record;
  const [answer, setAnswer] = useState<{ record: string; result: Result } | null>(null);
  const settled: Result = answer?.record === args.record && !(answer.result.state === "confirmed" && kept.kept) ? answer.result : { state: "none" };
  const setSettled = (result: Result) => setAnswer({ record: args.record, result });
  const confirmed = (send: Send): Result => ({ state: "confirmed", at: new Date().toISOString(), effects: [send.effect], remains: [] });
  const refused = (sentence: string): Result => ({ state: "refused", cap: "Change not made", sentence, draft: "kept" });
  const unknown = (send: Send): Result => ({ state: "unknown", requestId: send.key, cap: "Not confirmed", sentence: "Your input and original request are kept. Check before making another change.", check: { verb: "Check original request", note: "Reads the original receipt without sending again.", run: async () => {
    const end = kept.sending(true);
    try {
      const answer = await checkCommit(send.key);
      if (answer.state === "confirmed") {
        if (answer.receipt.action !== send.action || JSON.stringify(answer.receipt.subject_ids) !== JSON.stringify(send.subjects)) return unknown(send);
        end(); const result = confirmed(send); setSettled(result); await args.changed(send.body).catch(() => undefined); return result;
      }
      if (answer.state === "not_committed") {
        kept.setPending(null); const result = refused("The original request did not commit. Review the retained input before trying again."); setSettled(result); return result;
      }
    } catch { /* Keep the frozen request until its own receipt is known. */ }
    return unknown(send);
  } } });
  return { kept, busy, result: kept.pending && !busy ? unknown(kept.pending) : settled, run: async (body: B, key: string): Promise<Result> => {
    if (kept.pending || busy || kept.conflict) return kept.pending ? unknown(kept.pending) : refused("Compare the newer record before continuing.");
    const send: Send = { key, body, action: args.action, subjects: typeof args.subjects === "function" ? args.subjects(body) : args.subjects, effect: typeof args.effect === "function" ? args.effect(body) : args.effect };
    kept.setPending(send); const end = kept.sending(); setBusyRecord(args.record);
    try {
      await args.send(body, key); end(); const result = confirmed(send); setSettled(result); await args.changed(send.body).catch(() => undefined); return result;
    } catch (error) {
      if (error instanceof ApiError && error.status < 500 && error.status !== 408) {
        kept.setPending(null); const result = refused(error.message); setSettled(result); if (error.status === 409) await args.changed(send.body).catch(() => undefined); return result;
      }
      return unknown(send);
    } finally { setBusyRecord(record => record === args.record ? null : record); }
  } };
}
